'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

interface NavigationItem {
  label: string;
  href: string;
}

const Header = () => {
  const pathname = usePathname();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigationItems: NavigationItem[] = [
    { label: 'Home', href: '/' },
    { label: 'Our Mission', href: '/our-mission' },
    { label: 'Universities — Spain', href: '/universities-spain' },
    { label: 'Universities — Portugal', href: '/universities-portugal' },
    { label: 'Visa & Immigration', href: '/visa-immigration' },
    { label: 'Workforce Solutions', href: '/workforce-solutions' },
    { label: 'Contact Us', href: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const isActive = (href: string) => pathname === href;

  return (
    <>
      <a href="#main-content" className="skip-to-content">
        Skip to main content
      </a>

      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
          isScrolled
            ? 'shadow-card border-b border-white/20'
            : 'border-b border-white/10'
        }`}
        style={{
          height: '72px',
          backgroundColor: isScrolled ? '#2A7FA5' : 'transparent',
          backdropFilter: isScrolled ? 'blur(12px)' : 'none',
        }}
        role="banner"
      >
        <div className="max-w-7xl mx-auto px-6 h-full flex items-center justify-between">
          {/* Logo */}
          <Link
            href="/"
            className="flex flex-col group focus-visible:outline-none"
            aria-label="Nexus Vantage Home"
          >
            <span
              className="font-headline font-bold tracking-tight leading-none transition-colors duration-300"
              style={{ fontSize: '1.25rem', color: '#ffffff', letterSpacing: '-0.01em' }}
            >
              NEXUS VANTAGE
            </span>
            <span
              className="font-body font-normal transition-colors duration-300"
              style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.85)', letterSpacing: '0.12em', textTransform: 'uppercase' }}
            >
              Education &amp; Immigration Consultancy
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center space-x-6" role="navigation" aria-label="Main navigation">
            {navigationItems.slice(0, 6).map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className={`font-body text-sm font-medium transition-all duration-200 relative pb-1 focus-visible:outline-none ${
                  isActive(item.href)
                    ? 'text-white' :'text-white/80 hover:text-white'
                }`}
                aria-current={isActive(item.href) ? 'page' : undefined}
              >
                {item.label}
                <span
                  className={`absolute bottom-0 left-0 right-0 h-0.5 rounded-full transition-all duration-300 ${
                    isActive(item.href) ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'
                  }`}
                  style={{ backgroundColor: '#F5EDD6', transformOrigin: 'left' }}
                  aria-hidden="true"
                />
              </Link>
            ))}
            <Link
              href="/contact"
              className="ml-2 px-5 py-2.5 font-body font-semibold text-sm rounded-lg transition-all duration-250 hover:-translate-y-0.5 focus-visible:outline-none"
              style={{
                backgroundColor: '#F5EDD6',
                color: '#2A7FA5',
                border: '2px solid #F5EDD6',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = '#ede4cc';
                (e.currentTarget as HTMLElement).style.borderColor = '#ede4cc';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = '#F5EDD6';
                (e.currentTarget as HTMLElement).style.borderColor = '#F5EDD6';
              }}
            >
              Contact Us
            </Link>
          </nav>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="xl:hidden p-2 text-white hover:bg-white/10 rounded-lg transition-all duration-200 focus-visible:outline-none"
            aria-label={isMobileMenuOpen ? 'Close mobile menu' : 'Open mobile menu'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {isMobileMenuOpen ? (
                <>
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </>
              ) : (
                <>
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </>
              )}
            </svg>
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          id="mobile-menu"
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation menu"
          style={{
            position: 'fixed',
            top: '72px',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            backgroundColor: '#2A7FA5',
            overflowY: 'auto',
          }}
          className="xl:hidden animate-fade-in"
        >
          <nav className="flex flex-col h-full" role="navigation" aria-label="Mobile navigation">
            <div className="flex-1 px-6 py-8 space-y-1">
              {navigationItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center px-4 py-4 font-body text-base font-medium transition-all duration-200 rounded-lg border-l-4 ${
                    isActive(item.href)
                      ? 'text-white bg-white/15' :'text-white/80 hover:bg-white/10 hover:text-white border-transparent'
                  }`}
                  style={isActive(item.href) ? { borderLeftColor: '#F5EDD6' } : {}}
                  aria-current={isActive(item.href) ? 'page' : undefined}
                >
                  {item.label}
                </Link>
              ))}
            </div>
            <div className="px-6 py-6 border-t border-white/20">
              <p className="text-white/70 text-sm font-body mb-1">contact@nexusvantage.eu</p>
              <p className="text-white/70 text-sm font-body">Lisbon, Portugal</p>
            </div>
          </nav>
        </div>
      )}
    </>
  );
};

export default Header;